import { assertNotEmpty, type Optional, stringUtils } from '@festina-life/core'
import type { HierarchyPointNode } from '@visx/hierarchy'
import isEqual from 'lodash-es/isEqual'

import {
  isModelMetaType,
  type MetaModel,
  type MetaType,
  type ModelMetaType,
  type PropertyDescriptor,
} from '../../types/metaModel'
import {
  isSimpleTemplateField,
  type SubTemplateFieldValue,
  type TemplateField,
  type TemplateVersion,
} from '../../types/templateModel'
import { generateEmptyFieldValue } from '../../uiModels/createEmptyFieldHelpers'
import type {
  Cardinality,
  TreeChild,
  TreeNode,
  TypeNarrowedPathRegex,
} from './types'

/** Given a node, should return the sum of all of its descendant up until the leaf nodes. */
const countDescendants = <T>(node: HierarchyPointNode<T>): number =>
  (node.children || [])
    .map(countDescendants)
    .reduce((total, childDescendants) => total + childDescendants, 1)

/**
 * Given a D3 Hierarchy shown in horizontal, this function will change the y coordinates such that the nodes can be
 * presented similar to a file tree structure instead of D3's default layout from
 * https://github.com/d3/d3-hierarchy/blob/6498cc7/README.md#tree
 * */
export const flattenD3TreeNode = <T>({
  node,
  nodeHeightWithPadding,
  indentation,
}: {
  node: HierarchyPointNode<T>
  nodeHeightWithPadding: number
  indentation: number
}) => {
  if (node.depth === 0) {
    node.x = 0
    node.y = 0
  } else {
    const nthChild = node.parent!.children!.indexOf(node)
    const previousSiblings =
      (node.parent!.children || []).slice(0, nthChild) || []
    const totalPreviousSiblingDescendants = previousSiblings
      .map(countDescendants)
      .reduce((acc, depth) => acc + depth, 0)
    node.x =
      node.parent!.x +
      (1 + totalPreviousSiblingDescendants) * nodeHeightWithPadding

    node.y = node.parent!.y + indentation
  }

  node.children?.forEach((child) =>
    flattenD3TreeNode({ node: child, nodeHeightWithPadding, indentation }),
  )
}

/** Converts a Festina meta type to a tree node assigning a unique path to each node. */
export const metaTypeToTreeNode = ({
  metaType,
  name,
  templated = 'templated',
  visitedNodes = [],
}: {
  name?: Optional<string>
  metaType: ModelMetaType
  templated?: TreeNode['templated']
  visitedNodes?: MetaType[]
}): TreeNode => {
  const includes = visitedNodes.includes(metaType)

  visitedNodes.push(metaType)

  return {
    name: name ?? metaType.typeName,
    templated,
    isIdentifiableTemplate:
      isModelMetaType(metaType) &&
      metaType.stereoTypes.includes('IdentifiableSubTemplated'),
    documentation: metaTypeDocumentationWithProperties(metaType),
    children:
      includes || templated === 'otherTemplated'
        ? [] // don't drill down further
        : [
            ...metaTypeToTreeChildren({
              metaType,
              templated,
              visitedNodes,
            }),
          ],
  }
}

const standardNameFromType = (typeName: string, cardinality: Cardinality) =>
  `${typeName[0].toLocaleLowerCase()}${typeName.substring(1)}${cardinality === 'many' ? 's' : ''}`

const nodeName = ({ name, typeName, cardinality }: PropertyDescriptor) =>
  name !== standardNameFromType(typeName, cardinality)
    ? `${name}: ${typeName}`
    : typeName

const metaTypeToTreeChildren = ({
  metaType,
  visitedNodes,
  templated,
}: {
  metaType: ModelMetaType
  visitedNodes: MetaType[]
  templated: TreeNode['templated']
}): TreeChild[] => [
  ...(metaType.templateType?.properties ?? [])
    .filter(
      (property) =>
        property.type &&
        !visitedNodes.includes(property.type) &&
        property.stereoTypes.includes('OnlyTemplatedTemplateProp') &&
        property.typeName !== 'ConfigurationExpressionValue' &&
        !metaType.properties.some(
          (modelProperty) => modelProperty.name === property.name,
        ) &&
        !(
          metaType.superType as ModelMetaType | undefined
        )?.templateType?.properties.some(
          function isInheritedProperty(parentProperty) {
            return (
              parentProperty.name === property.name &&
              parentProperty.typeName === property.typeName
            )
          },
        ),
    )
    .map((property) => property.onlyTemplatedType)
    .filter(isModelMetaType)
    .map((templatedType): TreeChild => ({
      cardinality: 'one',
      association: 'composition',
      node: metaTypeToTreeNode({
        metaType: templatedType,
        templated: 'configurationOnly',
        visitedNodes: [...visitedNodes],
      }),
    }))
    .sort((a, b) => a.node.name.localeCompare(b.node.name)),
  ...metaType.properties
    .filter(
      (property) =>
        property.association === 'composition' &&
        isModelMetaType(property.type) &&
        !visitedNodes.includes(property.type),
    )
    .map((propertyDescriptor): TreeChild => {
      const propertyDescriptorType = propertyDescriptor.type as ModelMetaType //Asserted in the filter function
      let nodeTemplatedValue: TreeNode['templated'] = templated
      const templateTypeProperty = metaType.templateType?.properties.find(
        (templateProperty) => templateProperty.name === propertyDescriptor.name,
      )

      if (propertyDescriptorType.stereoTypes.includes('Templated')) {
        nodeTemplatedValue = 'otherTemplated'
      } else if (propertyDescriptor.stereoTypes.includes('NotTemplated')) {
        nodeTemplatedValue = 'notTemplated'
      } else if (
        templateTypeProperty &&
        templateTypeProperty.stereoTypes.includes('OnlyTemplatedTemplateProp')
      ) {
        nodeTemplatedValue = 'configurationOnly'
      }

      return {
        cardinality: propertyDescriptor.cardinality,
        association: 'composition',
        node: metaTypeToTreeNode({
          name: nodeName(propertyDescriptor),
          metaType: propertyDescriptorType,
          templated: nodeTemplatedValue,
          visitedNodes: [...visitedNodes],
        }),
      }
    })
    .sort((a, b) => a.node.name.localeCompare(b.node.name)),
  // Subtypes should come last, but are otherwise straightforward.
  ...(visitedNodes.length > 1 // Root level inherited nodes are not shown since template creation is done on the root type.
    ? metaType.subTypes.filter(isModelMetaType).map((subType): TreeChild => ({
        cardinality: 'one',
        association: 'subtype',
        node: metaTypeToTreeNode({
          metaType: subTypeWithoutInheritedProperties(subType),
          templated,
          visitedNodes: [...visitedNodes],
        }),
      }))
    : []),
]

const subTypeWithoutInheritedProperties = (
  subType: ModelMetaType,
): ModelMetaType => {
  const parent = subType.superType

  if (!parent || !isModelMetaType(parent)) {
    return subType
  }

  return {
    ...subType,
    properties: subType.properties.filter(
      (property) =>
        !parent.properties.some((parentProperty) =>
          isEqual(parentProperty, property),
        ),
    ),
  }
}

const metaTypeDocumentationWithProperties = (metaType: ModelMetaType) => {
  const documentation: TreeNode['documentation'] = [
    {
      // Property names should be unique, so the type name is wrapped in {} to avoid collisions.
      property: `{${metaType.typeName}}`,
      documentation: metaType.documentation ?? '',
    },
  ]

  for (const property of metaType.properties) {
    documentation.push({
      property: property.name,
      documentation: property.documentation ?? '',
    })
  }

  return documentation
}

export const getNodeHierarchy = (
  node: Optional<HierarchyPointNode<TreeNode>>,
): HierarchyPointNode<TreeNode>[] => {
  if (!node) {
    return []
  }

  return [...getNodeHierarchy(node.parent), node]
}

/**
 * Generates a list of regular expressions (regexes) representing the hierarchical path segments
 * for a given node in the TemplateVersion data model. Each regex in the returned array corresponds
 * to a step in the path from the root to the specified node, and is optionally narrowed to a specific
 * type name if present. This is used for traversing the TemplateVersion data model and finding all
 * of the paths that gradually comply with the regexes, allowing for type narrowing at each step.
 *
 * The returned array can be used to match and filter paths in the TemplateVersion structure,
 * supporting both standard and custom field names, as well as subtype associations.
 *
 * @param forNode - The hierarchy node for which to generate path regexes.
 * @param currentTemplateVersion - The TemplateVersion instance to use as the root for path generation.
 * @returns An array of objects, each containing a regex and an optional narrowed type name.
 */
export const getNodePathRegexes = ({
  forNode,
  currentTemplateVersion,
  metaModel,
}: {
  forNode: HierarchyPointNode<TreeNode>
  currentTemplateVersion: TemplateVersion
  metaModel: MetaModel
}) => {
  const nodeHierarchyWithoutRoot = getNodeHierarchy(forNode).slice(1)
  let pathRegex = new RegExp(
    `^\\{${stringUtils.escapeRegexp(currentTemplateVersion.versionInfo.identifier)}\\}`,
  )
  const partialRegexes: TypeNarrowedPathRegex[] = [
    { regex: pathRegex, typeName: undefined },
  ]

  for (const [nodeIndex, node] of nodeHierarchyWithoutRoot.entries()) {
    const nodeName = node.data.name
    const splitModelName = nodeName.split(':')

    const isStandardFieldNameForType = splitModelName.length === 1
    const customFieldName = !isStandardFieldNameForType
      ? splitModelName[0].trim()
      : undefined
    const typeName = isStandardFieldNameForType
      ? splitModelName[0].trim()
      : splitModelName[1].trim()

    const selfInParent = node.parent?.data.children.find(
      (child) => child.node === node.data,
    )

    assertNotEmpty(selfInParent)

    const standardFieldName = standardNameFromType(
      typeName,
      selfInParent?.cardinality,
    )

    const fieldName = customFieldName ?? standardFieldName
    const isLastNode = nodeIndex === nodeHierarchyWithoutRoot.length - 1

    if (selfInParent.association !== 'subtype') {
      if (node.data.isIdentifiableTemplate) {
        pathRegex = new RegExp(
          `${pathRegex.source}\\.${stringUtils.escapeRegexp(fieldName)}`,
        )

        partialRegexes.push({
          regex: pathRegex,
          typeName: undefined,
        })

        if (!isLastNode) {
          pathRegex = new RegExp(pathRegex.source + /\{\w+\}/.source)

          partialRegexes.push({
            regex: pathRegex,
            typeName: undefined,
          })
        }
      } else {
        pathRegex = new RegExp(
          `${pathRegex.source}\\.${stringUtils.escapeRegexp(fieldName)}`,
        )

        partialRegexes.push({
          regex: pathRegex,
          typeName: undefined,
        })

        if (!isLastNode) {
          const metaType = metaModel.types.get(
            typeName,
          ) as Optional<ModelMetaType>
          const subTypeNames =
            metaType?.subTypes.map((subType) => subType.typeName) ?? []
          const allTypes = [typeName, ...subTypeNames]
            .map(stringUtils.escapeRegexp)
            .join('|')

          const subtemplateIndex = new RegExp(`\\{(${allTypes})\\}`)
          pathRegex = new RegExp(pathRegex.source + subtemplateIndex.source)

          partialRegexes.push({
            regex: pathRegex,
            typeName: undefined,
          })
        }
      }
    } else if (partialRegexes.length > 1) {
      partialRegexes[partialRegexes.length - 1].typeName = typeName
    }
  }

  return partialRegexes
}

export const getCurrentFieldPathsForRegex = ({
  partialPathRegexes,
  isShowingEmptyValues,
  metaModel,
  field,
}: {
  partialPathRegexes: TypeNarrowedPathRegex[]
  isShowingEmptyValues: boolean
  metaModel: MetaModel
  field: TemplateField
}): string[] => {
  if (!partialPathRegexes.length) {
    return []
  }

  const [activePartialPath, ...remainingPartialPathRegexes] = partialPathRegexes
  const exactPathRegex = new RegExp(`${activePartialPath.regex.source}$`)

  const isExactMatch =
    exactPathRegex.test(field.path) &&
    (!activePartialPath.typeName ||
      activePartialPath.typeName ===
        field.modelPropertyDescriptor?.type?.typeName)

  const isFinalPath = !remainingPartialPathRegexes.length

  if (isExactMatch && isFinalPath && (field.value || isShowingEmptyValues)) {
    return [field.path]
  }

  if (isSimpleTemplateField(field)) {
    return []
  }

  // Subtemplated field

  const fieldValue =
    field.value ??
    (isShowingEmptyValues
      ? generateEmptyFieldValue({ field, meta: metaModel })
      : undefined)

  if (!fieldValue || !activePartialPath.regex.test(fieldValue.path)) {
    return []
  }

  return fieldValue.variants.flatMap((variant) =>
    getCurrentVariantPathsForRegex({
      partialPathRegexes: remainingPartialPathRegexes,
      isShowingEmptyValues,
      metaModel,
      variant,
    }),
  )
}

const getCurrentVariantPathsForRegex = ({
  partialPathRegexes,
  isShowingEmptyValues,
  metaModel,
  variant,
}: {
  partialPathRegexes: TypeNarrowedPathRegex[]
  isShowingEmptyValues: boolean
  metaModel: MetaModel
  variant: SubTemplateFieldValue
}): string[] => {
  const [activePartialPath, ...remainingPartialPathRegexes] = partialPathRegexes

  const isMatch =
    activePartialPath?.regex.test(variant.path) &&
    (!activePartialPath?.typeName ||
      activePartialPath.typeName === variant.modelType?.typeName)

  if (!isMatch) {
    return []
  }

  if (!remainingPartialPathRegexes.length) {
    return [variant.path]
  }

  return variant.fields.flatMap((field) =>
    getCurrentFieldPathsForRegex({
      partialPathRegexes: remainingPartialPathRegexes,
      isShowingEmptyValues,
      metaModel,
      field,
    }),
  )
}

export const getNodeLinks = ({
  forNode,
  currentTemplateVersion,
  metaModel,
  isShowingEmptyValues,
}: {
  forNode: HierarchyPointNode<TreeNode>
  currentTemplateVersion: TemplateVersion
  isShowingEmptyValues: boolean
  metaModel: MetaModel
}) => {
  const selectedPathRegex = getNodePathRegexes({
    currentTemplateVersion,
    forNode,
    metaModel,
  })

  return currentTemplateVersion.fields.flatMap((field) =>
    getCurrentFieldPathsForRegex({
      partialPathRegexes: selectedPathRegex.slice(1),
      field,
      isShowingEmptyValues,
      metaModel,
    }),
  )
}
